import { useContext, useEffect, useRef } from "react";
import { createPracticeQuiz } from "./assessments-page-helper";
import { PORTAL_TOAST_CONTAINER_ID } from "../../shared";
import { CoreButton, coreToast } from "@verity/components/core";
import i18n from "../../../i18n";
import apiClient from "../../../helpers/api-client";
import { initiateAuthenticationRedirect } from "../../../helpers/oauth";
import { PortalControllerContext } from "../../context";

export const PracticeQuizButton = ({ onFinish }: { onFinish?: () => void }) => {
	const { portalContext, action, setAction } = useContext(PortalControllerContext);
	const { lms, courseId, lmsProvidedCourseId } = portalContext;
	const buttonRef = useRef<HTMLButtonElement>(null);

	const handleCreateQuiz = async () => {
		coreToast.dismiss();
		const loadingToastId = coreToast.loading(i18n.t("portal.components.assessmentPage.practiceQuizCompTSX.createAss"), {
			containerId: PORTAL_TOAST_CONTAINER_ID,
		});
		try {
			// Check if lms is supported
			if (lms != "canvas" && lms != "desire2learn" && lms != "BlackboardLearn") {
				throw Error(i18n.t("portal.components.assessmentPage.practiceQuizCompTSX.noSupportLms")); // unsupported lms
			}
			// Get institute origin and api key id
			const instituteInfo = await apiClient.lmsInfo.getInstituteLmsInfo();
			if (!instituteInfo?.origin) {
				throw Error(i18n.t("portal.components.assessmentPage.practiceQuizCompTSX.lms"));
			} else if (!instituteInfo?.lmsApiClientId || instituteInfo.lmsApiClientId.trim() === "") {
				throw Error(i18n.t("portal.components.assessmentPage.practiceQuizCompTSX.noFeature"));
			} else if (courseId == null || !lmsProvidedCourseId) {
				throw Error("No course found.");
			}
			const oauthTokenExists = await apiClient.oauth.refreshStoredToken();
			if (oauthTokenExists || lms === "BlackboardLearn") {
				await createPracticeQuiz(instituteInfo.id, lms, lmsProvidedCourseId, instituteInfo.origin);
				coreToast.update(
					loadingToastId,
					i18n.t("portal.components.assessmentPage.practiceQuizCompTSX.success"),
					"success",
					{
						containerId: PORTAL_TOAST_CONTAINER_ID,
						autoClose: 2500,
					},
				);
				onFinish?.();
			}
			// Authorization Required
			else {
				localStorage.setItem(
					"initialPortalState",
					JSON.stringify({
						action: "createPracticeQuiz",
					}),
				);
				await initiateAuthenticationRedirect({
					courseId,
					callbackUrl: window.location.href,
				});
			}
		} catch (err) {
			coreToast.update(
				loadingToastId,
				`${i18n.t("portal.components.assessmentPage.practiceQuizCompTSX.quizCreateFail")}`,
				"error",
				{
					containerId: PORTAL_TOAST_CONTAINER_ID,
					autoClose: 2500,
				},
			);
			console.error(err);
		}
	};

	useEffect(() => {
		if (action === "createPracticeQuiz" && setAction) {
			void handleCreateQuiz().then(() => {
				setAction(undefined);
			});
		}
	}, []);

	return (
		<CoreButton
			ref={buttonRef}
			color="primary"
			id="create-assessment-button"
			style={{ alignSelf: "center" }}
			onClick={handleCreateQuiz}
			aria-label="Create Practice Assessment Button"
		>
			{i18n.t("portal.components.assessmentPage.practiceQuizCompTSX.createPracAss")}
		</CoreButton>
	);
};

export function extractHostname(url: string): string {
	if (!url.startsWith("http://") && !url.startsWith("https://")) {
		url = "https://" + url;
	}
	try {
		return new URL(url).hostname;
	} catch (error) {
		console.error("Invalid URL:", url);
		return "";
	}
}
